/**
 * System Store - Global Application State
 * 
 * Following the proven ENTITY_STATE_ARCHITECTURE pattern:
 * - Single mutation funnel via _applyLocal* helpers
 * - Actions API for all mutations
 * - CRDT sync for real-time collaboration
 * - Event emission for cross-component coordination
 * 
 * Manages:
 * - Recent spaces cache
 * - Active space tracking
 * - Dataspace context (Personal vs Org)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UISpace } from '../types/spaces';
import { createFileLogger } from '../utils/logger';
import { spaceService } from '../services/spaceService';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';

const log = createFileLogger('SYSTEM_STORE', { enabled: true });

// Types
export interface DataspaceContext {
  type: 'personal' | 'org';
  orgId: string | null;
  orgName: string | null;
}

export interface Org {
  _id: string;
  id: string;
  entityType: 'Org';
  name: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  skeleton: {
    '@type'?: string;
    displayName: string;
    slug: string;
    description?: string;
    logo_url?: string;
    owner_user_id: string;
    member_user_ids: string[];
    admin_user_ids: string[];
    guest_user_ids: string[];
    orggroup_ids: string[];
    root_group_ids: string[];
    billing_plan: string;
    billing_status?: string;
    billing_schema_version?: string;
    seat_count?: number;
    shared_credit_balance: number;
    monthly_credit_refresh: number;
  };
  acl: any[];
}

interface MutationOptions {
  log?: boolean;
  source?: 'local' | 'remote' | 'event';
  syncCRDT?: boolean;
}

interface SystemState {
  // Space management
  recentSpaces: UISpace[];
  activeSpaceId: string | null;
  spacesCacheTime: number | null;
  spaceVisitTimestamps: Record<string, number>; // Maps spaceId to last visit timestamp
  
  // Dataspace context
  currentContext: DataspaceContext;
  userOrgs: Org[];
  lastVisitedSpaces: Record<string, string>; // Maps dataspace key to spaceId
  
  // Loading states
  isLoadingSpaces: boolean;
  isLoadingContext: boolean;
  error: string | null;
  
  // Payment gate
  paymentGate: {
    isOpen: boolean;
    orgId: string | null;
    orgName: string | null;
    seatCount: number;
  };
}

export interface SystemStore extends SystemState {
  // Internal mutation helpers (single funnel)
  _applyLocalSpaceUpdate: (space: UISpace, opts?: MutationOptions) => void;
  _applyLocalSpaceDelete: (spaceId: string, opts?: MutationOptions) => void;
  _applyLocalContextUpdate: (context: DataspaceContext, opts?: MutationOptions) => void;
  
  // Actions (canonical API)
  actions: {
    createSpace: (params: { name: string; templateType?: string; settings?: any; skipDefaultWindow?: boolean; skipProjectLimit?: boolean }) => Promise<UISpace>;
    updateSpace: (spaceId: string, updates: Partial<UISpace>) => Promise<void>;
    deleteSpace: (spaceId: string) => Promise<void>;
    switchSpace: (spaceId: string) => void;
    refreshRecentSpaces: () => Promise<void>;
    
    switchDataspace: (context: DataspaceContext) => Promise<void>;
    switchToPersonal: () => Promise<void>;
    switchToOrg: (orgId: string) => Promise<void>;
    loadUserOrgs: () => Promise<void>;
  };
  
  // Getters
  getSpaceById: (spaceId: string) => UISpace | null;
  getCurrentDataspaceSpaces: () => UISpace[];
  getLastVisitedSpace: (contextKey?: string) => string | null;
  
  // Helpers
  setLastVisitedSpace: (spaceId: string) => void;
  clearError: () => void;
  showPaymentGate: (orgId: string) => void;
  hidePaymentGate: () => void;
}

export const useSystemStore = create<SystemStore>()(
  persist(
    (set, get) => {
      // ===== INTERNAL MUTATION HELPERS =====
      
      /**
       * _applyLocalSpaceUpdate - Single funnel for space mutations
       * Handles: normalization, CRDT sync, state update
       */
      const _applyLocalSpaceUpdate = (space: UISpace, opts: MutationOptions = {}) => {
        const options = {
          log: true,
          source: 'local' as const,
          syncCRDT: true,
          ...opts
        };
        
        log.info('[_applyLocalSpaceUpdate] Updating space:', {
          spaceId: space.id,
          spaceName: space.name,
          source: options.source
        });
        
        // Update or add space to recentSpaces
        set((state) => {
          const existingIndex = state.recentSpaces.findIndex(s => s.id === space.id);
          let newSpaces: UISpace[];
          
          if (existingIndex >= 0) {
            // Update existing space
            newSpaces = [...state.recentSpaces];
            newSpaces[existingIndex] = space;
          } else {
            // Add new space at the beginning
            newSpaces = [space, ...state.recentSpaces].slice(0, 10); // Keep top 10
          }
          
          return {
            recentSpaces: newSpaces,
            spacesCacheTime: Date.now()
          };
        });
        
        // TODO: Add CRDT sync when space-level CRDT is implemented
        // if (options.syncCRDT && options.source === 'local') {
        //   systemCRDTService.updateSpace(space);
        // }
        
        log.info('[_applyLocalSpaceUpdate] Space updated in cache');
      };
      
      /**
       * _applyLocalSpaceDelete - Single funnel for space deletions
       */
      const _applyLocalSpaceDelete = (spaceId: string, opts: MutationOptions = {}) => {
        const options = {
          log: true,
          source: 'local' as const,
          syncCRDT: true,
          ...opts
        };
        
        log.info('[_applyLocalSpaceDelete] Deleting space:', { spaceId, source: options.source });
        
        set((state) => ({
          recentSpaces: state.recentSpaces.filter(s => s.id !== spaceId),
          activeSpaceId: state.activeSpaceId === spaceId ? null : state.activeSpaceId
        }));
        
        // TODO: Add CRDT sync when space-level CRDT is implemented
        
        log.info('[_applyLocalSpaceDelete] Space removed from cache');
      };
      
      /**
       * _applyLocalContextUpdate - Single funnel for dataspace context changes
       */
      const _applyLocalContextUpdate = (context: DataspaceContext, opts: MutationOptions = {}) => {
        const options = {
          log: true,
          source: 'local' as const,
          syncCRDT: false, // Context is persisted to backend, not CRDT
          ...opts
        };
        
        log.info('[_applyLocalContextUpdate] Updating dataspace context:', {
          type: context.type,
          orgId: context.orgId,
          source: options.source
        });
        
        set({ currentContext: context });
        
        // Clear spaces cache when context changes (need to reload for new dataspace)
        set({ recentSpaces: [], spacesCacheTime: null });
        
        log.info('[_applyLocalContextUpdate] Context updated, spaces cache cleared');
      };
      
      // ===== ACTIONS API =====
      
      const actions = {
        /**
         * Create a new space in the current dataspace
         */
        createSpace: async (params: { name: string; templateType?: string; settings?: any; skipDefaultWindow?: boolean; skipProjectLimit?: boolean }) => {
          log.info('[actions.createSpace] Creating space:', params);
          
          try {
            // Get current dataspace context to determine org_id
            const currentContext = get().currentContext;
            const orgId = currentContext.type === 'org' ? currentContext.orgId : null;
            
            log.info('[actions.createSpace] Creating space in dataspace:', {
              dataspaceType: currentContext.type,
              orgId,
              spaceName: params.name,
              hasSettings: !!params.settings
            });
            
            // 1. Call backend with org_id and settings
            const newSpace = await spaceService.createSpace(params.name, params.templateType, undefined, orgId, params.settings, params.skipProjectLimit);
            
            log.info('[actions.createSpace] Space created on backend:', {
              spaceId: newSpace.id,
              name: newSpace.name,
              org_id: newSpace.org_id
            });
            
            // 2. Update via mutation funnel
            get()._applyLocalSpaceUpdate(newSpace, {
              log: true,
              source: 'local',
              syncCRDT: true
            });
            
            // 2b. ✅ CRITICAL: Also add to entityStore so it appears in recent spaces
            try {
              const { default: useEntityStore } = await import('./entityStore');
              const spaceAsEntity = {
                _id: newSpace.id,
                id: newSpace.id,
                type: 'space',
                entityType: 'Space',
                name: newSpace.name,
                created_at: newSpace.createdAt,
                updated_at: newSpace.updatedAt,
                skeleton: {
                  '@type': 'Space',
                  windows: newSpace.windows,
                  settings: newSpace.settings,
                  contextEntities: newSpace.contextEntities,
                  contextExcerpts: newSpace.contextExcerpts
                },
                org_id: newSpace.org_id
              } as any;
              
              useEntityStore.getState().addEntity(spaceAsEntity);
              log.info('[actions.createSpace] ✅ Added space to entityStore for recent spaces list');
            } catch (entityStoreError) {
              log.warn('[actions.createSpace] Failed to add space to entityStore (non-critical):', entityStoreError);
            }
            
            // 3. Set as active space
            set({ activeSpaceId: newSpace.id });
            
            // 4. Set as last visited for current dataspace
            get().setLastVisitedSpace(newSpace.id);
            
            // 5. Create a default "New Window" for the space (BEFORE navigation, so it's in backend)
            // ✨ INSTANT NAVIGATION: Skip for pending spaces (hero input flow)
            if (!params.skipDefaultWindow) {
              try {
                log.info('[actions.createSpace] Creating default New Window for space:', newSpace.id);
              
              // Dynamically import to avoid circular dependencies
              const { default: useEntityStore } = await import('./entityStore');
              const { spaceService } = await import('../services/spaceService');
              
              // Create a Window entity (appears in sidebar, has navigation history)
              const windowEntity = await useEntityStore.getState().actions.createEntity({
                entityType: 'Window',
                name: 'New Window',
                spaceId: newSpace.id,
                skeleton: {
                  '@type': 'Window',
                  displayName: 'New Window',
                  createdAt: new Date().toISOString(),
                  lastAccessedAt: new Date().toISOString(),
                }
              });
              
              const entityId = windowEntity._id || windowEntity.id;
              const entityLlmId = windowEntity.llmId;
              log.info('[actions.createSpace] Window entity created:', { entityId, entityLlmId });
              
              // Create window object and update the space DIRECTLY on backend
              const windowId = `window-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
              const windowData = {
                id: windowId,
                type: 'window' as const,
                appType: 'entity-view',
                entityId: entityId,
                entityLlmId: entityLlmId,
                entity: windowEntity,
                position: { x: window.innerWidth / 2 - 400, y: window.innerHeight / 2 - 300 },
                size: { width: 800, height: 600 },
                applicationState: {
                  windowEntityId: entityId,
                  isNew: true // Mark as new for launcher state
                },
                title: 'New Window',
              };
              
              // Update the space on backend with the window
              const updatedSpace = {
                ...newSpace,
                windows: {
                  [windowId]: windowData
                },
                windowOrder: [windowId],
                activeWindowId: windowId
              };
              
              // Persist the window to backend BEFORE navigation
              await spaceService.updateSpace(newSpace.id, updatedSpace.windows, updatedSpace.settings);
              
              // Update local cache with the window
              get()._applyLocalSpaceUpdate(updatedSpace, {
                log: true,
                source: 'local',
                syncCRDT: false // Already persisted above
              });
              
              // Link the window ID back to the Window entity
              await useEntityStore.getState().actions.updateEntity(entityId, {
                skeleton: {
                  ...windowEntity.skeleton,
                  windowId
                }
              });
              
                log.info('[actions.createSpace] ✅ Default window created and persisted:', { 
                  windowId, 
                  windowEntityId: entityId,
                  spaceId: newSpace.id
                });
              } catch (windowError) {
                log.warn('[actions.createSpace] Failed to create default window (non-fatal):', windowError);
                // Don't fail space creation if window creation fails
              }
            } else {
              log.info('[actions.createSpace] ⏭️ Skipping default window creation (skipDefaultWindow=true)');
            }
            
            // 6. Emit event (for analytics, notifications, etc.)
            try {
              const event = new CustomEvent('system:space:created', {
                detail: { space: newSpace }
              });
              window.dispatchEvent(event);
            } catch (eventError) {
              log.warn('[actions.createSpace] Failed to emit event:', eventError);
            }
            
            log.info('[actions.createSpace] ✅ Space created successfully:', newSpace.id);
            return newSpace;
          } catch (error) {
            log.error('[actions.createSpace] Failed to create space:', error);
            throw error;
          }
        },
        
        /**
         * Update a space
         */
        updateSpace: async (spaceId: string, updates: Partial<UISpace>) => {
          log.info('[actions.updateSpace] Updating space:', { spaceId, updates });
          
          try {
            // Find existing space
            const existingSpace = get().recentSpaces.find(s => s.id === spaceId);
            if (!existingSpace) {
              throw new Error(`Space ${spaceId} not found in cache`);
            }
            
            // Merge updates
            const updatedSpace = { ...existingSpace, ...updates };
            
            // Update via mutation funnel
            get()._applyLocalSpaceUpdate(updatedSpace, {
              log: true,
              source: 'local',
              syncCRDT: true
            });
            
            log.info('[actions.updateSpace] ✅ Space updated successfully');
          } catch (error) {
            log.error('[actions.updateSpace] Failed to update space:', error);
            throw error;
          }
        },
        
        /**
         * Delete a space
         */
        deleteSpace: async (spaceId: string) => {
          log.info('[actions.deleteSpace] Deleting space:', { spaceId });
          
          try {
            // TODO: Call backend delete endpoint when available
            // await spaceService.deleteSpace(spaceId);
            
            // Delete via mutation funnel
            get()._applyLocalSpaceDelete(spaceId, {
              log: true,
              source: 'local',
              syncCRDT: true
            });
            
            log.info('[actions.deleteSpace] ✅ Space deleted successfully');
          } catch (error) {
            log.error('[actions.deleteSpace] Failed to delete space:', error);
            throw error;
          }
        },
        
        /**
         * Switch to a different space
         */
        switchSpace: (spaceId: string) => {
          log.info('[actions.switchSpace] Switching to space:', { spaceId });
          
          set({ activeSpaceId: spaceId });
          get().setLastVisitedSpace(spaceId);
          
          // Emit event
          try {
            const event = new CustomEvent('system:space:switched', {
              detail: { spaceId }
            });
            window.dispatchEvent(event);
          } catch (eventError) {
            log.warn('[actions.switchSpace] Failed to emit event:', eventError);
          }
        },
        
        /**
         * Refresh recent spaces from backend
         */
        refreshRecentSpaces: async () => {
          log.info('[actions.refreshRecentSpaces] Fetching recent spaces from backend');
          
          set({ isLoadingSpaces: true, error: null });
          
          try {
            const allSpaces = await spaceService.fetchRecentSpaces();
            const currentContext = get().currentContext;
            
            // Filter by current dataspace
            const filteredSpaces = allSpaces.filter(space => {
              const spaceOrgId = space.org_id;
              const isPersonalSpace = !spaceOrgId;
              const isOrgSpace = !!spaceOrgId;
              
              if (currentContext.type === 'personal') {
                return isPersonalSpace;
              }
              
              if (currentContext.type === 'org' && currentContext.orgId) {
                return isOrgSpace && spaceOrgId === currentContext.orgId;
              }
              
              return false;
            });
            
            log.info('[actions.refreshRecentSpaces] Fetched and filtered spaces:', {
              total: allSpaces.length,
              filtered: filteredSpaces.length,
              dataspaceType: currentContext.type
            });
            
            // Update cache
            set({
              recentSpaces: filteredSpaces.slice(0, 10),
              spacesCacheTime: Date.now(),
              isLoadingSpaces: false
            });
          } catch (error) {
            log.error('[actions.refreshRecentSpaces] Failed to fetch spaces:', error);
            set({ 
              error: error instanceof Error ? error.message : 'Failed to fetch spaces',
              isLoadingSpaces: false 
            });
            throw error;
          }
        },
        
        /**
         * Switch dataspace context
         */
        switchDataspace: async (context: DataspaceContext) => {
          log.info('[actions.switchDataspace] Switching dataspace:', context);
          
          set({ isLoadingContext: true, error: null });
          
          try {
            // Persist context to backend
            const data = await fetchService(`${API_BASE_URL}/user/context`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                context_type: context.type,
                org_id: context.orgId
              })
            });
            
            // Update via mutation funnel
            get()._applyLocalContextUpdate({
              type: data.type,
              orgId: data.org_id || null,
              orgName: data.org_name || null
            }, {
              source: 'local'
            });
            
            // Switch entityStore dataspace
            try {
              const { default: useEntityStore } = await import('./entityStore');
              const dataspaceKey = data.type === 'org' && data.org_id ? data.org_id : 'personal';
              
              useEntityStore.setState({ 
                currentDataspaceKey: dataspaceKey,
                entities: [],
                initialized: false,
                lifecycle: 'uninitialized'
              });
              
              log.info('[actions.switchDataspace] EntityStore dataspace key updated:', dataspaceKey);
              
              // Re-initialize with new CRDT room
              const { getCurrentUserId } = await import('../utils/userIdUtils');
              const userId = getCurrentUserId();
              
              const crdtRoomId = data.type === 'org' && data.org_id
                ? `org:${data.org_id}`
                : `user:${userId}`;
              
              await useEntityStore.getState().initialize(crdtRoomId);
              log.info('[actions.switchDataspace] EntityStore re-initialized with room:', crdtRoomId);
            } catch (entityStoreError) {
              log.error('[actions.switchDataspace] Failed to update entityStore:', entityStoreError);
            }
            
            // Refresh spaces for new dataspace
            await get().actions.refreshRecentSpaces();
            
            set({ isLoadingContext: false });
            
            log.info('[actions.switchDataspace] ✅ Dataspace switched successfully');
          } catch (error) {
            log.error('[actions.switchDataspace] Failed to switch dataspace:', error);
            set({ 
              error: error instanceof Error ? error.message : 'Failed to switch dataspace',
              isLoadingContext: false 
            });
            throw error;
          }
        },
        
        /**
         * Switch to personal dataspace (delegates to dataspaceContextStore which
         * resolves the user's actual personal org id from the backend).
         */
        switchToPersonal: async () => {
          const { useDataspaceContextStore } = await import('./dataspaceContextStore');
          await useDataspaceContextStore.getState().switchToPersonal();
        },
        
        /**
         * Switch to org dataspace
         */
        switchToOrg: async (orgId: string) => {
          const org = get().userOrgs.find(o => o._id === orgId || o.id === orgId);
          
          if (!org) {
            throw new Error(`Organization ${orgId} not found`);
          }
          
          // Check billing status
          const billingStatus = org.skeleton.billing_status;
          if (billingStatus === 'payment_required' || billingStatus === 'past_due') {
            log.info('[actions.switchToOrg] Payment required, showing payment gate');
            get().showPaymentGate(orgId);
            return;
          }
          
          const currentContext = get().currentContext;
          if (currentContext.type === 'org' && currentContext.orgId === orgId) {
            log.info('[actions.switchToOrg] Already in org dataspace:', orgId);
            return;
          }
          
          await get().actions.switchDataspace({
            type: 'org',
            orgId: org._id || org.id,
            orgName: org.skeleton.displayName || org.name
          });
        },
        
        /**
         * Load user's organizations
         */
        loadUserOrgs: async () => {
          log.info('[actions.loadUserOrgs] Loading user organizations');
          
          set({ isLoadingContext: true, error: null });
          
          try {
            const orgs: Org[] = await fetchService(`${API_BASE_URL}/orgs`, {
              method: 'GET'
            });
            
            set({ userOrgs: orgs, isLoadingContext: false });
            
            log.info('[actions.loadUserOrgs] ✅ Loaded organizations:', orgs.length);
          } catch (error) {
            log.error('[actions.loadUserOrgs] Failed to load organizations:', error);
            set({ 
              error: error instanceof Error ? error.message : 'Failed to load organizations',
              isLoadingContext: false 
            });
            throw error;
          }
        }
      };
      
      return {
        // Initial state
        recentSpaces: [],
        activeSpaceId: null,
        spacesCacheTime: null,
        spaceVisitTimestamps: {},
        
        currentContext: {
          type: 'personal',
          orgId: null,
          orgName: null
        },
        userOrgs: [],
        lastVisitedSpaces: {},
        
        isLoadingSpaces: false,
        isLoadingContext: false,
        error: null,
        
        paymentGate: {
          isOpen: false,
          orgId: null,
          orgName: null,
          seatCount: 1
        },
        
        // Internal helpers
        _applyLocalSpaceUpdate,
        _applyLocalSpaceDelete,
        _applyLocalContextUpdate: (context: DataspaceContext, opts: MutationOptions = {}) => {
          set({ currentContext: context });
          set({ recentSpaces: [], spacesCacheTime: null });
          log.info('[_applyLocalContextUpdate] Context updated');
        },
        
        // Actions
        actions,
        
        // Getters
        getSpaceById: (spaceId: string) => {
          return get().recentSpaces.find(s => s.id === spaceId) || null;
        },
        
        getCurrentDataspaceSpaces: () => {
          const { recentSpaces, currentContext } = get();
          
          return recentSpaces.filter(space => {
            const spaceOrgId = space.org_id;
            const isPersonalSpace = !spaceOrgId;
            const isOrgSpace = !!spaceOrgId;
            
            if (currentContext.type === 'personal') {
              return isPersonalSpace;
            }
            
            if (currentContext.type === 'org' && currentContext.orgId) {
              return isOrgSpace && spaceOrgId === currentContext.orgId;
            }
            
            return false;
          });
        },
        
        getLastVisitedSpace: (contextKey?: string) => {
          const { currentContext, lastVisitedSpaces } = get();
          const key = contextKey || (currentContext.type === 'personal' ? 'personal' : currentContext.orgId || '');
          
          return key ? lastVisitedSpaces[key] || null : null;
        },
        
        // Helpers
        setLastVisitedSpace: (spaceId: string) => {
          const { currentContext, lastVisitedSpaces, spaceVisitTimestamps } = get();
          const contextKey = currentContext.type === 'personal' ? 'personal' : currentContext.orgId || '';
          const timestamp = Date.now();
          
          if (contextKey) {
            set({
              lastVisitedSpaces: {
                ...lastVisitedSpaces,
                [contextKey]: spaceId
              },
              spaceVisitTimestamps: {
                ...spaceVisitTimestamps,
                [spaceId]: timestamp
              }
            });
            log.info('[setLastVisitedSpace] Updated last visited space:', { contextKey, spaceId, timestamp });
          }
        },
        
        clearError: () => set({ error: null }),
        
        showPaymentGate: (orgId: string) => {
          const org = get().userOrgs.find(o => o._id === orgId || o.id === orgId);
          
          if (org) {
            set({
              paymentGate: {
                isOpen: true,
                orgId: org._id || org.id,
                orgName: org.skeleton.displayName || org.name,
                seatCount: org.skeleton.seat_count || 1
              }
            });
          }
        },
        
        hidePaymentGate: () => {
          set({
            paymentGate: {
              isOpen: false,
              orgId: null,
              orgName: null,
              seatCount: 1
            }
          });
        }
      };
    },
    {
      name: 'system-store',
      partialize: (state) => ({
        currentContext: state.currentContext,
        lastVisitedSpaces: state.lastVisitedSpaces,
        spaceVisitTimestamps: state.spaceVisitTimestamps,
        // Don't persist spaces cache - it should refresh on load
      })
    }
  )
);

export default useSystemStore;

